// Default-deny capture policy for the desktop local capture producer.
//
// The producer never decides on its own that a call may be recorded. An owner integration (the call
// client or web app) passes in the server-verified call and capability state it already holds; this
// module only reads it. Anything missing, unknown, stale or belonging to another identity denies.
// The spool in main checks the server again on `begin`; this is the renderer-side half, which also
// drives pause, resume and stop while the call is in progress.

/** Server call state, mirroring `Call.state` in the API contract. */
export type ServerCallState = 'created' | 'ringing' | 'active' | 'held' | 'ended' | 'failed';
/** Server recording state, mirroring `Call.recording_state` in the API contract. */
export type ServerRecordingState = 'off' | 'awaiting_consent' | 'recording' | 'paused' | 'processing' | 'ready' | 'failed';

/**
 * The verified state an owner integration hands to the producer, built from the server's `Call`,
 * `CallCapture` and `desktop.capture` capability. Every field comes from the server; none may be
 * filled in from local UI state.
 */
export interface DesktopCapturePolicyInput {
  /** Signed-in identity the state was read under. */
  tenant_id: string;
  user_id: string;
  /** Opaque value that changes on every sign-in, sign-out, tenant switch or token-family change. */
  auth_generation: string;
  call_id: string;
  call_state: ServerCallState;
  recording_state: ServerRecordingState;
  /** Subset of the server's `CallCapture` for this call; null when the server returned none. */
  capture: {
    /** Increases with every capture state change; older snapshots are ignored. */
    version: number;
    policy_verified: boolean;
    all_consented: boolean;
    observed_state: ServerRecordingState;
    policy_version: string | null;
  } | null;
  /** The `desktop.capture` capability for this user; null when the server returned none. */
  desktop_capture: { status: string; allowed: boolean } | null;
}

export interface CaptureIdentity {
  tenant_id: string;
  user_id: string;
  auth_generation: string;
}

export type PolicyDenyReason =
  | 'no_policy'
  | 'invalid_policy'
  | 'other_call'
  | 'call_not_active'
  | 'call_ended'
  | 'recording_not_active'
  | 'recording_paused'
  | 'consent_not_verified'
  | 'capability_not_allowed'
  | 'capability_not_enabled';

/**
 * - `record`: capture may run.
 * - `pause`: the server paused recording; keep the item open but write nothing.
 * - `end`: the call ended normally; flush what was recorded and finalize.
 * - `stop`: recording is no longer allowed (consent, capability, policy); write nothing more.
 */
export type PolicyDecision =
  | { action: 'record' }
  | { action: 'pause'; reason: 'recording_paused' }
  | { action: 'end'; reason: 'call_ended' }
  | { action: 'stop'; reason: PolicyDenyReason };

const IDENT = /^[A-Za-z0-9_-]{1,128}$/;
const CALL_STATES = new Set<string>(['created', 'ringing', 'active', 'held', 'ended', 'failed']);
const RECORDING_STATES = new Set<string>(['off', 'awaiting_consent', 'recording', 'paused', 'processing', 'ready', 'failed']);

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Structural check; a malformed snapshot is treated exactly like no snapshot. */
export function isValidPolicyInput(v: unknown): v is DesktopCapturePolicyInput {
  if (!isRecord(v)) return false;
  for (const k of ['tenant_id', 'user_id', 'auth_generation', 'call_id'] as const) {
    if (typeof v[k] !== 'string' || !IDENT.test(v[k] as string)) return false;
  }
  if (typeof v.call_state !== 'string' || !CALL_STATES.has(v.call_state)) return false;
  if (typeof v.recording_state !== 'string' || !RECORDING_STATES.has(v.recording_state)) return false;
  if (v.capture !== null) {
    const c = v.capture;
    if (!isRecord(c)) return false;
    if (typeof c.version !== 'number' || !Number.isSafeInteger(c.version) || c.version < 0) return false;
    if (typeof c.policy_verified !== 'boolean' || typeof c.all_consented !== 'boolean') return false;
    if (typeof c.observed_state !== 'string' || !RECORDING_STATES.has(c.observed_state)) return false;
    if (c.policy_version !== null && typeof c.policy_version !== 'string') return false;
  }
  if (v.desktop_capture !== null) {
    const d = v.desktop_capture;
    if (!isRecord(d) || typeof d.status !== 'string' || typeof d.allowed !== 'boolean') return false;
  }
  return true;
}

export function sameIdentity(a: CaptureIdentity, b: CaptureIdentity): boolean {
  return a.tenant_id === b.tenant_id && a.user_id === b.user_id && a.auth_generation === b.auth_generation;
}

/**
 * What the server state allows for `callId`. Recording is allowed only when the call is live, both the
 * call and its capture record say `recording`, consent is verified for every participant, and the
 * `desktop.capture` capability is allowed and enabled. Everything else is a pause, an end or a stop.
 */
export function decide(input: unknown, callId: string): PolicyDecision {
  if (input === null || input === undefined) return { action: 'stop', reason: 'no_policy' };
  if (!isValidPolicyInput(input)) return { action: 'stop', reason: 'invalid_policy' };
  if (input.call_id !== callId) return { action: 'stop', reason: 'other_call' };
  if (input.call_state === 'ended' || input.call_state === 'failed') return { action: 'end', reason: 'call_ended' };
  if (input.call_state !== 'active' && input.call_state !== 'held') return { action: 'stop', reason: 'call_not_active' };
  const cap = input.desktop_capture;
  if (!cap || cap.allowed !== true) return { action: 'stop', reason: 'capability_not_allowed' };
  if (cap.status !== 'enabled') return { action: 'stop', reason: 'capability_not_enabled' };
  const c = input.capture;
  if (!c || c.policy_verified !== true || c.all_consented !== true) return { action: 'stop', reason: 'consent_not_verified' };
  // A pause on either record pauses; only agreement on `recording` records.
  if (input.recording_state === 'paused' || c.observed_state === 'paused') return { action: 'pause', reason: 'recording_paused' };
  if (input.recording_state !== 'recording' || c.observed_state !== 'recording') return { action: 'stop', reason: 'recording_not_active' };
  return { action: 'record' };
}
